import { utils } from "../../Foundation";

export class LMSRibbon {
    constructor(element) {
        this._refs = {
            el: element,
            collapse: element.querySelector('.contents-collapse_button'),
            contents: element.querySelector('.overview__contents'),
            menu: element.querySelector('.overview__menu'),
            api_url: element.getAttribute('data-api-url'),
            state: {
                isCollapse: true,
                totalItems: 0,
                // Data coming from the template
                achievementItems: [],
                certificateItems: [],
                certificateHeader: {
                    'earnedCertificate': {
                        'label': '',
                        'earned': 0,
                        'total': 0
                    },
                    'earnedPoints': {
                        'label': '0',
                        'total': 0
                    }
                }
            },
            settings: {
                translations: {
                    achievements: 'Achievements',
                    certificates: 'Certificates',
                    collapse: 'Collapse',
                    expand: 'Expand',
                }
            }
        }

        if(this._refs.collapse) {
            this._refs.collapse.addEventListener('click', this._toggleCollapse.bind(this));
            this._refs.collapse.addEventListener('keyup', (e) => { 
                if(e.key === 'Enter') {
                    this._toggleCollapse();
                }
            })
        }

        // Initiate build template
        this._init();
    }

    /** 
     * Functions 
     **/
    async _init() {
        await this._fetchDataFromApi();

        // Check if there are contents to display 
        if(this._refs.state.achievementItems.length == 0 && this._refs.state.certificateItems.length == 0) return this._initiateNoContent();
        // Build achievements overview
        await this._createAchievementItems();
        await this._createCertificateItems();
        await this._toggleCollapse();
    }

    async _fetchDataFromApi() {
        const apiUrl = this._refs.api_url;
        await fetch(apiUrl)
            .then((response) => response.json())
            .then((json) => {
                if(json) {
                    this._updateState(json);
                }
            }).catch( err => {
                console.error('Fetch data from API failed.');
                console.error('Error: ', err);
            });
    }

    // State management
    _updateState(input_data) {
        if(input_data.achievementItems) {
            this._refs.state.achievementItems = input_data.achievementItems.data;
        }
        if(input_data.certificateItems) {
            this._refs.state.certificateItems = input_data.certificateItems.data;
            this._refs.state.certificateHeader = input_data.certificateItems.header;
            this._refs.state.totalItems = input_data.certificateItems.data.length;
        }
        if(input_data.settings && input_data.settings.translations) {
            this._refs.settings.translations = {...this._refs.settings.translations, ...input_data.settings.translations};
        }
    }

    // No contents
    _initiateNoContent() {
        this._refs.contents.remove();
        this._refs.menu.remove();
        this._refs.collapse.remove();
        this._refs.el.classList.add('no-content')
    }

    // Achievements
    _createAchievementItems() {
        if(this._refs.state.achievementItems.length > 0) {
            this._appendAchievementsMenu();
            
            // Create content parent div
            utils.createElement('div', {
                'class': 'contents-achievements content d-flex',
            }, this._refs.contents);

            // Create content items
            let templateConstructor = '';
            for(const item of this._refs.state.achievementItems) {
                templateConstructor += this._achievementItemTemplate(item);
            }
            this._refs.contents.querySelector('.contents-achievements').innerHTML = templateConstructor;

            // Add event listener
            if(this._refs.achievements) {
                this._refs.achievements.addEventListener('click', this._showAchievements.bind(this));
                this._refs.achievements.addEventListener('keyup', (e) => { 
                    if(e.key === 'Enter') {
                        this._showAchievements();
                    }
                })
            }
        }
    }

    _appendAchievementsMenu() {
        const template = `
        <div class="menu__item achievements active" tabindex="0" aria-role="button">
            ${this._refs.settings.translations.achievements}
        </div>
        `;
        this._refs.menu.insertAdjacentHTML('beforeend', template);
        this._refs.achievements = this._refs.el.querySelector('.achievements');
    }

    _achievementItemTemplate(item) {
        // Add custom icons
        const itemIcon = item.icon.showIcon ? `
        <div class="content__icon d-flex align-items-center">
            <div class="${item.icon.iconName} icon eva"></div>
        </div>` : '';

        return `
        <div class="ribbon__content d-flex align-items-center">
            ${itemIcon}
            <div class="content__item">
                <div class="h4 bold-font">${item.headline.label}</div>
                <p>${item.description.label}</p>
            </div>
        </div>`;
    }

    // Certificates
    _createCertificateItems() {
        if(this._refs.state.certificateItems.length > 0) {
            this._appendCertificateMenu();

            const isHide = this._refs.achievements ? 'contents-certificates hide' : 'contents-certificates';
            // Create content parent div
            const certificateContents = utils.createElement('div', {
                'class': `content d-flex ${isHide}`,
            }, this._refs.contents);

            // Create content items
            let templateConstructor = '';
            for(const item of this._refs.state.certificateItems) {
                templateConstructor += this._certificateItemTemplate(item);
            }

            // Append header
            const headerTemplate = this._certificateHeaderTemplate();
            certificateContents.innerHTML = headerTemplate + templateConstructor;

            // Add event listener
            if(this._refs.certificates) {
                this._refs.certificates.addEventListener('click', this._showCertificates.bind(this));
                this._refs.certificates.addEventListener('keyup', (e) => { 
                    if(e.key === 'Enter') {
                        this._showCertificates();
                    }
                })
            }
        }
    }

    _appendCertificateMenu() {
        const isActive = this._refs.achievements ? 'certificates' : 'certificates active';
        const template = `
        <div class="menu__item ${isActive}" tabindex="0" aria-role="button">
            ${this._refs.settings.translations.certificates}
        </div>
        `;
        this._refs.menu.insertAdjacentHTML('beforeend', template);
        this._refs.certificates = this._refs.el.querySelector('.certificates');
    }

    _certificateHeaderTemplate() {
        const headers = this._refs.state.certificateHeader;
        return `
        <div class="ribbon__header">
            <div class="col-md-6">
                <div class="h3">${headers.earnedCertificate.label}: <span class="highlight">${headers.earnedCertificate.earned}/${headers.earnedCertificate.total}</span></div>
            </div>
            <div class="col-md-6">
                <div class="h3">${headers.earnedPoints.label}: <span class="highlight">${headers.earnedPoints.total}</span></div>
            </div>
        </div>
        `;
    }

    _certificateItemTemplate(item) {
        return `
        <div class="ribbon__content content d-flex align-items-center">
            <div class="content__icon d-flex align-items-center">
                <div class="icon-student icon eva"></div>
            </div>
            <div class="content__item">
                <div class="h4 bold-font">${item.headline.label}</div>
                <p>${item.points.label}: ${item.points.points}</p>
                <p>${item.courseDate.label}: ${item.courseDate.dateLabel}</p>
                <p>${item.expieryDate.label}: ${item.expieryDate.dateLabel}</p>
            </div>
            <div class="content__cta">
                <a class="btn-primary is-download" href="${item.download.url}">${item.download.label}</a>
            </div>
        </div>
        `;
    }


    /**
     * Event listener
     **/
    _toggleCollapse() {
        this._refs.contents.classList.toggle('collapse');
        this._refs.collapse.querySelector('.eva-arrow-ios-upward-outline').classList.toggle('hide');
        this._refs.collapse.querySelector('.eva-arrow-ios-downward-outline').classList.toggle('hide');

        
        const collapseText = this.isCollapse ? this._refs.settings.translations.expand : this._refs.settings.translations.collapse;
        this._refs.collapse.querySelector('.collapse_button').innerText = collapseText;
    }

    _showAchievements() {
        if(this._refs.achievements) {
            this._refs.achievements.classList.add('active');
            this._refs.contents.querySelector('.contents-achievements').classList.remove('hide');
        }
        if(this._refs.certificates) {
            this._refs.certificates.classList.remove('active');
            this._refs.contents.querySelector('.contents-certificates').classList.add('hide');
        }
    }
    
    _showCertificates() {
        if(this._refs.achievements) {
            this._refs.achievements.classList.remove('active');
            this._refs.contents.querySelector('.contents-achievements').classList.add('hide');
        }
        if(this._refs.certificates) {
            this._refs.certificates.classList.add('active');
            this._refs.contents.querySelector('.contents-certificates').classList.remove('hide');
        }
    }

    get isCollapse() {
        return this._refs.state.isCollapse = !this._refs.state.isCollapse;
    }
}